import {
  DollarSign,
  Users,
  Maximize2,
  Clock,
  ChevronUp,
  ChevronDown,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { formatDate } from "@/utils/format/format-date";
import { Label } from "@/components/ui/label";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import type { RankedSpace, SpaceAdjustment, SpaceStaleStatus } from "../types";
import { getMonthlyPrice } from "../utils/spaceAdjustments";

interface SpaceAdjustmentCardProps {
  space: RankedSpace;
  adjustment: SpaceAdjustment;
  isRecommended: boolean;
  onExtraValueChange: (value: number) => void;
  onToggleRecommended: () => void;
  staleStatus?: SpaceStaleStatus;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
  canMoveUp?: boolean;
  canMoveDown?: boolean;
  hideRecommendedButton?: boolean;
}

export function SpaceAdjustmentCard({
  space,
  adjustment,
  isRecommended,
  onExtraValueChange,
  onToggleRecommended,
  staleStatus,
  onMoveUp,
  onMoveDown,
  canMoveUp = true,
  canMoveDown = true,
  hideRecommendedButton = false,
}: SpaceAdjustmentCardProps) {
  const monthlyPrice = getMonthlyPrice(space);
  const isVipOffice =
    space.source === "vip-office" && space.origin === "integration";

  return (
    <div
      className="rounded-xl border p-4 space-y-3"
      style={{
        borderColor: isRecommended ? "#F9E10B" : "#E8E6E3",
        background: isRecommended ? "rgba(249,225,11,0.04)" : undefined,
      }}
    >
      {staleStatus === "unavailable" && (
        <div
          className="text-xs font-medium px-3 py-2 rounded-lg -mx-1 -mt-1 mb-1"
          style={{ background: "#FEF3C7", color: "#92400E" }}
        >
          ⚠️ Sala não retornada na busca atual — verifique antes de salvar
        </div>
      )}
      {staleStatus === "data-changed" && (
        <div
          className="text-xs font-medium px-3 py-2 rounded-lg -mx-1 -mt-1 mb-1"
          style={{ background: "#DBEAFE", color: "#1E40AF" }}
        >
          Dados desta sala foram atualizados — ajustes de preço podem estar
          defasados. Verifique!
        </div>
      )}
      <div className="flex items-center justify-between gap-2">
        <span
          className="font-semibold text-sm truncate"
          style={{ color: "#1A1814" }}
        >
          {space.roomName}
        </span>
        <div className="flex items-center gap-1.5 shrink-0">
          {(onMoveUp || onMoveDown) && (
            <TooltipProvider>
              <div className="flex items-center">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      onClick={onMoveUp}
                      disabled={!canMoveUp}
                      aria-label="Mover para cima"
                      className="flex items-center justify-center w-6 h-6 rounded transition-colors disabled:opacity-20 hover:bg-gray-100"
                      style={{ color: "#9E9A94" }}
                    >
                      <ChevronUp className="w-3.5 h-3.5" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Mover esta sala para cima na proposta</p>
                  </TooltipContent>
                </Tooltip>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      onClick={onMoveDown}
                      disabled={!canMoveDown}
                      aria-label="Mover para baixo"
                      className="flex items-center justify-center w-6 h-6 rounded transition-colors disabled:opacity-20 hover:bg-gray-100"
                      style={{ color: "#9E9A94" }}
                    >
                      <ChevronDown className="w-3.5 h-3.5" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Mover esta sala para baixo na proposta</p>
                  </TooltipContent>
                </Tooltip>
              </div>
            </TooltipProvider>
          )}
          {!hideRecommendedButton && (
            <button
              type="button"
              onClick={onToggleRecommended}
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-semibold transition-all"
              style={
                isRecommended
                  ? { background: "#F9E10B", color: "#1A1814" }
                  : {
                      border: "1.5px dashed #C8C4BE",
                      color: "#9E9A94",
                      background: "transparent",
                    }
              }
            >
              {isRecommended ? "★ Recomendado" : "☆ Marcar como recomendado"}
            </button>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2">
        <span className="text-xs" style={{ color: "#9E9A94" }}>
          {space.spaceName}
        </span>
        <span className="text-xs" style={{ color: "#78736D" }}>
          {space.address}
        </span>
      </div>

      <div
        className="flex items-center gap-4 text-xs"
        style={{ color: "#78736D" }}
      >
        <span className="inline-flex items-center gap-1">
          <Users className="w-3 h-3" />
          {space.capacity} posições
        </span>
        {space.sizeM2 != null && (
          <span className="inline-flex items-center gap-1">
            <Maximize2 className="w-3 h-3" />
            {space.sizeM2} m²
          </span>
        )}
        <span className="inline-flex items-center gap-1">
          <span
            className="w-4 h-4 rounded flex items-center justify-center flex-shrink-0"
            style={{ background: "#F0EEEB" }}
          >
            <DollarSign className="w-2.5 h-2.5" style={{ color: "#78736D" }} />
          </span>
          {isVipOffice ? "~" : ""}R$ {monthlyPrice.toLocaleString("pt-BR")}/mês
          {isVipOffice && <span style={{ color: "#B8B4AE" }}>(estimado)</span>}
        </span>
      </div>

      {space.availabilityDate && (
        <div
          className="flex items-center gap-4 text-xs"
          style={{ color: "#78736D" }}
        >
          <span className="inline-flex items-center gap-1">
            <Clock className="w-3 h-3" />
            Data de disponibilidade: {formatDate(space.availabilityDate)}
          </span>
        </div>
      )}

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1">
          <Label className="text-xs font-medium" style={{ color: "#78736D" }}>
            Valor extra (R$)
          </Label>
          <Input
            type="number"
            min={0}
            value={adjustment.extraValue}
            onChange={(e) =>
              onExtraValueChange(
                e.target.value === "" ? 0 : Number(e.target.value),
              )
            }
          />
          <p className="text-[10px]" style={{ color: "#9E9A94" }}>
            Acréscimo sobre o preço base, cobrado como créditos extras — o total
            e a margem recalculam automaticamente
          </p>
        </div>
        <div className="space-y-1">
          <Label className="text-xs font-medium" style={{ color: "#78736D" }}>
            Margem (%)
          </Label>
          <div
            aria-label="Margem calculada"
            className="flex h-10 w-full items-center rounded-md border px-3 py-2 text-sm"
            style={{
              borderColor: "#E8E6E3",
              background: "#F0EEEB",
              color: "#43403D",
            }}
          >
            {adjustment.margin.toLocaleString("pt-BR")}%
          </div>
        </div>
      </div>

      <div
        className="flex items-center justify-between text-[11px] font-medium rounded-lg px-3 py-2"
        style={{ background: "#F0EEEB", color: "#43403D" }}
      >
        <span className="inline-flex items-center gap-1">
          {adjustment.credits} cred. +{adjustment.extraCredits} extras
        </span>
        <span className="font-bold" style={{ color: "#1A1814" }}>
          R$ {adjustment.totalPrice.toLocaleString("pt-BR")}
        </span>
      </div>
    </div>
  );
}
